import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtDateTime } from "@/lib/time";
import { fieldNoteWrapClass } from "@/components/field-notes/PunchNotesReviewList";

export function FieldNotesAdminTable({
  notes,
  onProcess,
  isProcessing,
  canAcknowledge = true,
  /** null = unrestricted (admin / manager-only list already team-scoped). Set = dual edit allowlist. */
  acknowledgeableUserIds = null,
  showAll = false,
  hideEmpty = false,
}: {
  notes: import("@/lib/api").FieldNote[];
  onProcess?: (id: number) => void;
  isProcessing: boolean;
  canAcknowledge?: boolean;
  acknowledgeableUserIds?: Set<number> | null;
  showAll?: boolean;
  hideEmpty?: boolean;
}) {
  if (notes.length === 0) {
    if (hideEmpty) return null;
    return (
      <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
        {showAll ? "No field notes match the current filters." : "No unacknowledged field notes."}
      </div>
    );
  }
  // Card stack (not a wide multi-column table) so note text wraps and dates keep full
  // width on phone/desktop instead of being squished by a long Note column.
  return (
    <div className="w-full max-w-full min-w-0 space-y-3">
      {notes.map((note) => {
        const mayAckThisNote =
          canAcknowledge &&
          !note.processed &&
          !!onProcess &&
          (acknowledgeableUserIds == null || acknowledgeableUserIds.has(note.user_id));
        const viewOnlyUnacked =
          canAcknowledge &&
          !note.processed &&
          acknowledgeableUserIds != null &&
          !acknowledgeableUserIds.has(note.user_id);
        return (
        <div
          key={note.id}
          className={`w-full max-w-full min-w-0 rounded-lg border p-3 sm:p-4 ${
            !note.processed ? "border-destructive/25 bg-destructive/5" : "bg-card"
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-2 min-w-0">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              {!note.processed ? (
                <Badge variant="destructive" className="shrink-0">
                  Unacknowledged
                </Badge>
              ) : (
                <Badge variant="secondary" className="shrink-0">
                  Acknowledged
                </Badge>
              )}
              {viewOnlyUnacked && (
                <Badge variant="outline" className="shrink-0 text-muted-foreground">
                  View only — not on your team
                </Badge>
              )}
              {note.correction_type && (
                <Badge variant="outline" className="shrink-0 max-w-full whitespace-normal text-left">
                  {note.correction_type}
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {note.qb_record_url ? (
                <a
                  href={note.qb_record_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-primary underline-offset-4 hover:underline"
                >
                  Open in QuickBase
                </a>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {note.qb_record_id ? "QuickBase linked" : "Syncing…"}
                </span>
              )}
              {mayAckThisNote && (
                <Button
                  variant="default"
                  size="sm"
                  disabled={isProcessing}
                  onClick={() => onProcess!(note.id)}
                >
                  Acknowledge
                </Button>
              )}
            </div>
          </div>

          <div className="mt-2 min-w-0">
            <div className="text-sm font-medium break-words [overflow-wrap:anywhere]">
              {note.employee_name || note.user_email || "Unknown submitter"}
            </div>
            {note.employee_name && note.user_email && (
              <div className="text-xs text-muted-foreground break-all">{note.user_email}</div>
            )}
          </div>

          <div className={`mt-2 ${fieldNoteWrapClass}`}>{note.additional_note || "—"}</div>

          {note.reason_for_correction && (
            <div className={`mt-1 text-xs text-muted-foreground ${fieldNoteWrapClass}`}>
              <strong className="text-foreground/80">Reason:</strong> {note.reason_for_correction}
            </div>
          )}

          <div className="mt-3 grid grid-cols-1 gap-1 text-xs text-muted-foreground sm:grid-cols-2 min-w-0">
            <div className="min-w-0 break-words">
              <span className="font-medium text-foreground/70">Submitted: </span>
              <span className="whitespace-nowrap">{fmtDateTime(note.submitted_at)}</span>
            </div>
            {note.original_datetime && (
              <div className="min-w-0 break-words">
                <span className="font-medium text-foreground/70">Original: </span>
                <span className="whitespace-nowrap">{fmtDateTime(note.original_datetime)}</span>
              </div>
            )}
            {note.corrected_datetime && (
              <div className="min-w-0 break-words">
                <span className="font-medium text-foreground/70">Corrected: </span>
                <span className="whitespace-nowrap">{fmtDateTime(note.corrected_datetime)}</span>
              </div>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );
}

